// PE-30 — the SLOT GROUP (superset/circuit) actions. Split out of
// programEditActions.ts, which this changeset pushed to 535/500 lines: the
// same extraction programEditActionsNote.ts already made when that file was
// at the cap, and this is the concern PE-30 extended, so it is the one that
// earns its own module. Pure re-export of two `(draft, …) => Draft` actions
// through the barrel — no behaviour change, no caller touched.
import { addSetRow, updateSlot } from "./programEditActions.js";
import type { Draft, SlotGroupDraft } from "./programEdit.js";

export const setSlotGroup = (
  draft: Draft,
  sIdx: number,
  slotIdx: number,
  group: SlotGroupDraft | null,
): Draft => updateSlot(draft, sIdx, slotIdx, { group });

/** PE-30 — pad every member of `groupId` up to the group's longest set-row
 *  count. A superset renders as interleaved rounds, so members with different
 *  counts leave orphaned rounds; `buildPayload` already forces this symmetry
 *  ON SAVE (programEditBuild.ts, "Force symmetric supersets"), which meant the
 *  editor showed one thing and stored another. This applies the SAME rule in
 *  the draft, through `addSetRow`. ⭐ The claim "the two cannot drift apart"
 *  was FALSE when first written (PE-30 review): the save-time pad was a
 *  hand-rolled `{ ...last }` that also copied the previous set's note and its
 *  translations, which `addSetRow` blanks. Both now call the one
 *  `seedNextSetRow`, so the sentence is true because the code makes it true,
 *  not because a comment says so.
 *
 *  ⛔ Never trims: dropping a prescribed row would lose volume, same reason
 *  the save path only ever pads. ⚠️ Deliberately NOT the logger's SS-3 rule
 *  (pad with EMPTY rows, loggerGroupReducer 'align_group_sets'): a live
 *  session's blank row is one the athlete fills as they go, while a program's
 *  blank row would be a prescription nobody wrote. The program's own
 *  convention wins on the program's own draft. */
export const alignGroupSetRows = (
  draft: Draft,
  sIdx: number,
  groupId: string,
): Draft => {
  const slots = draft.sessions[sIdx]?.slots ?? [];
  const memberIdxs: number[] = [];
  slots.forEach((slot, i) => {
    if (slot.group?.id === groupId) memberIdxs.push(i);
  });
  const target = memberIdxs.reduce(
    (max, i) => Math.max(max, slots[i]!.setRows.length),
    0,
  );
  return memberIdxs.reduce((d, slotIdx) => {
    let next = d;
    while ((next.sessions[sIdx]?.slots[slotIdx]?.setRows.length ?? 0) < target) {
      const grown = addSetRow(next, sIdx, slotIdx);
      // addSetRow returns the draft untouched when the slot is gone; without
      // this the loop would never terminate.
      if (grown === next) break;
      next = grown;
    }
    return next;
  }, draft);
};
